import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NavigationSkipped, Router } from '@angular/router';
import { ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { OrderViewModel } from '@order/models/order-view-model';
import { OrderAiAgentService } from '@order/services/order-aiagent.service';
import { OrderService } from '@order/services/order.service';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { Crumb } from '@shared/components/breadcrumbs/breadcrumbs.component';
import { FileFormControl } from '@shared/components/import-order/file-form-control/file-form-control';
import { mimeTypeValidator } from '@shared/components/import-order/mime-type-validator/mime-type-validator';
import { PageTitle } from '@shared/constants/page-titles.enum';
import { PATHS } from '@shared/constants/paths';
import { FEATURES } from '@shared/constants/splitio-features';
import { ImageOrderingApiResponse } from '@shared/models/ai-agent-response';
import { UserPhoto } from '@shared/services/photo/model/photo.model';
import { PhotoService } from '@shared/services/photo/photo.service';
import { Subscription, take } from 'rxjs';
import { ImageOrderingAnalyticsService } from './tracking/analytics/image-ordering-analytics.service';
import { ImageOrderingResult } from '@shared/models/ai-agent-response';
@Component({
  selector: 'app-image-ordering',
  templateUrl: './image-ordering.page.html',
  styleUrls: ['./image-ordering.page.scss'],
})
export class ImageOrderingPage
  implements ViewWillEnter, ViewWillLeave, OnDestroy
{
  //default values
  static MAX_FILE_SIZE_MB = 10;
  static ALLOWED_FILE_TYPES = ['jpg', 'jpeg', 'png'];
  static MAX_FILE_SIZE = ImageOrderingPage.MAX_FILE_SIZE_MB * 1024 * 1024;
  breadcrumbs: Crumb[];
  @ViewChild('imageInput') imageInput: ElementRef<HTMLInputElement>;
  form: FormGroup = new FormGroup({
    file: new FileFormControl('', [mimeTypeValidator(), Validators.required]),
    format: new FormControl('', Validators.required),
  });
  hasCameraAccess = true;
  formatControlSub: Subscription;
  fileControlSub: Subscription;
  loading = false;
  apiResp: ImageOrderingApiResponse = null;
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;
  showFileSizeError: boolean;
  showFileTypeError: boolean;
  isNewFormatEnabled: boolean;

  private routerNavigationSkipSub: Subscription;
  private currentOrderSub$: Subscription;
  private vm: OrderViewModel;
  private orderControlSub$: Subscription;
  protected imageOrderingResults: ImageOrderingResult[] = [];
  constructor(
    readonly panAppState: PanAppState,
    readonly platformService: PlatformService,
    private http: HttpClient,
    private router: Router,
    private orderService: OrderService,
    private orderAIService: OrderAiAgentService,
    private photoService: PhotoService,
    private translateService: TranslateService,
    private imageOrderingAnalyticsService: ImageOrderingAnalyticsService,
  ) {}

  ngOnDestroy(): void {
    if (this.orderControlSub$) this.orderControlSub$.unsubscribe();
  }

  ionViewWillEnter(): void {
    this.isDesktop =
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop;
    this.isTablet =
      this.platformService.platformType ===
      this.platformService.platformEnum.tablet;
    this.isMobile =
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile;

    this.routerNavigationSkipSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationSkipped) {
        this.loading = false;
        this.apiResp = null;
        this.showFileSizeError = false;
        this.showFileTypeError = false;
      }
    });

    this.panAppState
      .feature$([
        FEATURES.split_dynamic_order_image_ordering_format_enabled.name,
      ])
      .pipe(take(1))
      .subscribe(isEnabled => {
        this.isNewFormatEnabled = isEnabled;
      });

    this.currentOrderSub$ = this.orderService
      .loadOrderViewModel$()
      .subscribe(vm => (this.vm = vm));

    this.orderControlSub$ = this.orderService
      .getImageOrderingConfig$()
      .subscribe(config => {
        ImageOrderingPage.MAX_FILE_SIZE_MB = config.maxFileSizeMB;
        ImageOrderingPage.MAX_FILE_SIZE =
          ImageOrderingPage.MAX_FILE_SIZE_MB * 1024 * 1024;
        ImageOrderingPage.ALLOWED_FILE_TYPES = config.allowedFileTypes;
      });

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myOrders',
        url: '/desktop/my-orders-view',
        active: false,
      },
      {
        label: 'Image Ordering',
        url: '',
        active: true,
      },
    ];

    this.panAppState.setPageTitle(
      this.translateService.instant(PageTitle.imageOrdering),
    );

    this.imageOrderingAnalyticsService.trackPageLoad();
  }

  ionViewWillLeave(): void {
    if (!!this.routerNavigationSkipSub)
      this.routerNavigationSkipSub.unsubscribe();
    if (!!this.currentOrderSub$) this.currentOrderSub$.unsubscribe();

    this.imageOrderingResults = [];
  }

  onImageChange(event) {
    const fileInput = event.target as HTMLInputElement;
    this.form.controls['file'].setValue(fileInput.files);
    this.form.controls['file'].markAsTouched();
    const file = event.target.files[0];
    this.showFileSizeError = false;
    this.showFileTypeError = false;
    if (file) {
      const fileExtension = file.name?.split('.')?.pop()?.toLowerCase();
      this.showFileSizeError = file.size > ImageOrderingPage.MAX_FILE_SIZE;
      this.showFileTypeError =
        !ImageOrderingPage.ALLOWED_FILE_TYPES.includes(fileExtension);
      if (!this.showFileSizeError && !this.showFileTypeError) {
        this.searchByImage(file, fileExtension);
      } else {
        this.loading = false;
        this.apiResp = { error: true } as ImageOrderingApiResponse;
      }
    }
  }

  browseImages() {
    this.imageInput.nativeElement.click();
  }

  async searchByImage(image: any, fileExtension: string) {
    this.loading = true;
    this.apiResp = null;
    this.showFileSizeError = false;
    this.showFileTypeError = false;
    const formData = new FormData();
    formData.append('file', image);
    this.imageOrderingAnalyticsService.trackStartUpload(fileExtension);
    await this.orderAIService
      .searchByImage(
        formData,
        this.vm?.orderItemState?.entities,
        this.isNewFormatEnabled,
      )
      .subscribe((data: ImageOrderingApiResponse) => {
        this.loading = false;
        this.apiResp = data;
        this.imageInput.nativeElement.value = '';
        if (
          this.apiResp?.matches?.length > 0 ||
          this.apiResp?.suggestions?.length > 0
        ) {
          this.imageOrderingAnalyticsService.trackFinishSuccessProcessing(
            fileExtension,
          );
          const newResult: ImageOrderingResult = {
            imageId: `img_${Date.now()}`,
            matches: this.apiResp?.matches,
            fileName: image.name,
            fileType: fileExtension,
          };

          this.imageOrderingResults.push(newResult);
        }
      });
  }

  navigateToCartPage() {
    this.addAllProductsToCart();
    this.router.navigate([PATHS.ORDER]);
  }

  navigateToListsPage() {
    this.router.navigate([PATHS.LISTS]);
  }

  private addAllProductsToCart(): void {
    // Group all matches by matchedProductNumber to avoid race conditions
    const groupedMatches = new Map<string, any>();

    this.imageOrderingResults.forEach(result => {
      if (result.matches && result.matches.length > 0) {
        result.matches.forEach(match => {
          const productKey = this.isNewFormatEnabled
            ? match.matchedProductNumber?.toString()
            : match.productNumber?.toString();

          if (productKey) {
            if (groupedMatches.has(productKey)) {
              // Add quantities to existing product
              const existing = groupedMatches.get(productKey);
              existing.quantityCases =
                (existing.quantityCases || 0) + (match.quantityCases || 0);
              existing.quantityEaches =
                (existing.quantityEaches || 0) + (match.quantityEaches || 0);
            } else {
              // Create new product entry
              groupedMatches.set(productKey, {
                ...match,
                quantityCases: match.quantityCases || 0,
                quantityEaches: match.quantityEaches || 0,
              });
            }
          }
        });
      }
    });

    // Convert grouped matches back to array and send to service
    if (groupedMatches.size > 0) {
      const aggregatedMatches = Array.from(groupedMatches.values());
      this.orderAIService.addProductsToCart(
        aggregatedMatches,
        this.vm?.orderItemState?.entities,
        this.isNewFormatEnabled,
      );
    }
  }

  async takePhoto() {
    const photo: UserPhoto = await this.photoService.addPhoto();
    const imgFile = this.photoService.getFileFromBase64(
      photo.base64,
      photo.extension,
    );
    this.searchByImage(imgFile, photo.extension);
    this.photoService.deletePhoto(photo);
  }

  handleDeleteProduct(event: {
    imageId: string;
    matchedProductNumber: string;
    quantityCases: number;
    quantityEaches: number;
  }): void {
    this.imageOrderingResults = this.imageOrderingResults
      .map(result => {
        if (result.imageId === event.imageId) {
          return {
            ...result,
            matches: result.matches.filter(
              match =>
                match.matchedProductNumber !== event.matchedProductNumber,
            ),
          };
        }
        return result;
      })
      .filter(result => result.matches.length > 0);
  }

  handleChangeQuantity(event: {
    imageId: string;
    matchedProductNumber: string;
    newQuantityCases: number;
    newQuantityEaches: number;
  }): void {
    this.imageOrderingResults = this.imageOrderingResults
      .map(result => {
        if (result.imageId === event.imageId) {
          return {
            ...result,
            matches: result.matches
              .map(match => {
                if (match.matchedProductNumber === event.matchedProductNumber) {
                  return {
                    ...match,
                    quantityCases: event.newQuantityCases,
                    quantityEaches: event.newQuantityEaches,
                  };
                }
                return match;
              })
              .filter(
                match => match.quantityCases > 0 || match.quantityEaches > 0,
              ),
          };
        }
        return result;
      })
      .filter(result => result.matches.length > 0);
  }
}
